import React, { Component, useEffect } from "react";

import style from "./Home.module.scss";
import { useDispatch, useSelector } from 'react-redux'
import MetaTags from 'react-meta-tags';

import {
  Header,
  MainBanner,
  ProjectsBanner,
  SectionCareer,
  SectionDesign,
  SectionServices
} from "../../components";

import {
  banner as bannerFallback,
  projects as projectsFallback,
  services as servicesFallback,
} from '../../api/data';
import useLocale from '../../hooks/useLocale';
import { loadHomeData } from '../../store/slices/home';
import { loadServicesData } from '../../store/slices/services';
import { normalizeMedia, mapSeo } from '../../api/strapi';

const FALLBACK_HERO = (bannerFallback.banner || []).map(slide => ({
  title: slide.title,
  image: slide.image,
  buttonText: slide.buttonText || 'Learn More',
  link: slide.link || '/en/about',
}))

function HookWrapper({ render }) {
  const locale = useLocale();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadHomeData(locale));
    dispatch(loadServicesData(locale));
  }, [dispatch, locale]);

  return render(loadSeo(), loadHomeBanner(), loadServicesList(), locale);
}

function loadSeo() {
  const { data } = useSelector(state => state.home)
  return mapSeo(data && data.seo)
}

function loadHomeBanner() {
  const { data } = useSelector(state => state.home)

  // Always keep a usable hero so an empty/incomplete Strapi Home Page
  // does not leave a blank first viewport.
  if (!data) {
    return {
      banner: FALLBACK_HERO,
      about: null,
      projects: null,
      projects_slider: [],
    }
  }

  const design = data.designSection
  const projects = data.projectsSection
  const designImage = normalizeMedia(design && design.image)
  const rawSlides = (projects && projects.slides) || []
  const projectSlides = rawSlides
    .map(item => {
      const media = item && item.image ? item.image : item
      return normalizeMedia(media)
    })
    .filter(Boolean)

  const heroFromCms = (data.heroSlides || [])
    .map(slide => ({
      title: slide.title,
      image: (normalizeMedia(slide.image) || {}).file_url,
      buttonText: slide.buttonLabel,
      link: slide.link,
    }))
    .filter(slide => slide.image)

  return {
    banner: heroFromCms.length ? heroFromCms : FALLBACK_HERO,
    about: design ? {
      title: design.title,
      description: design.description,
      image: designImage && designImage.file_url,
      linkButton: (design.button && design.button.label) || 'Learn More',
      link: (design.button && design.button.url) || '/en/about',
    } : null,
    projects: projects ? {
      title: projects.title,
      description: projects.description,
      linkButton: (projects.button && projects.button.label) || 'Latest Project',
      link: (projects.button && projects.button.url) || '/en/projects',
    } : null,
    projects_slider: projectSlides.map(m => ({ image: m.file_url })),
  }
}

function loadServicesList() {
  const { data } = useSelector(state => state.services)
  const items = (data && data.items) || []
  return items.map(item => ({
    id: item.id,
    title: item.title,
    slug: item.slug,
    image: normalizeMedia(item.image) || { file_url: null },
  }))
}

class Homepage extends Component {
  render() {
    return (
       <HookWrapper render={ (seo, home, servicesList, locale) =>
          <div className={style.container}>
            <MetaTags>
              <title>{seo.title}</title>
              <meta name="description" content={seo.meta_description} />
              <meta name="keyword" content={seo.meta_keyword} />
              <meta property="og:title" content={seo.title} />
            </MetaTags>
            <Header />
            <MainBanner data={{ banner: FALLBACK_HERO }} banners={home} />
            <SectionDesign title="" content="" data={home} />
            <ProjectsBanner
              title={projectsFallback.title}
              caption={projectsFallback.caption}
              data={projectsFallback}
              projects={home}
            />
            <SectionServices data={servicesFallback} services={servicesList} locale={locale}/>
            <SectionCareer locale={locale}/>
          </div>
       }/>
    );
  }
}

export default Homepage;
